import { Navigate, Outlet, Route, useParams, useLocation } from "react-router";
import { ProductDetails } from "./ProductDetails/ProductDetails";
import { ProductList } from "./ProductList";
import { UpdateQuestionnaire } from "./ProductForm/UpdateProduct";
import { CreateQuestionnaire } from "./ProductForm/CreateProduct";
import {
    CREATE,
    MODIFY,
    PRODUCT,
    PRODUCT_ACTIVITY_DATA,
    useFoodpilotContext,
    VIEW,
    VIEW_SSQ,
} from "src/context/FoodpilotContext";
import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { Entities, Slugs } from "src/context/ProtectedContext";
import { ProductScores } from "./ProductDetails/ProductScores";
import { Questionnaire } from "./ProductDetails/Questionnaire/Questionnaire";
import { ProductInformations } from "./ProductDetails/ProductInformations";
import { SectionResolver } from "./SectionResolver";
import { ProductQuestionnaire } from "./ProductDetails/ProductQuestionnaire";
import { Summary } from "./ProductDetails/Summary/Summary";
import { Ingredients } from "./ProductDetails/Entities/Ingredients/Ingredients";
import { Packagings } from "./ProductDetails/Entities/Packagings/Packagings";
import { DimensionTreeSwitch } from "src/components/ScoreDetail/Dimensions/DimensionTreeSwitch";
import { ProductValueChain } from "./ProductDetails/ProductValueChain";
import { ValueChainList } from "src/pages/ValueChain/ValueChainList";
import { ProductMetadataContextProvider } from "src/context/Product/Metadata/provider";
import { ProductOneScoreDetailProvider } from "./ProductDetails/Scores/ProductScoreContextProvider";
import { ProductScoresContextProvider } from "src/context/Product/Scores/provider";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { QuestionnaireUpdate } from "./ProductDetails/Questionnaire/QuestionnaireUpdate";
import { Loading } from "src/components/utils/Loading";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { ProductActivitiesData } from "./ProductDetails/ProductActivitiesData";
import { ProductScoreDetail } from "./ProductDetails/Scores/ProductScoreDetail";
import { ProductScoreDistribution } from "./ProductDetails/Scores/ProductScoreDistribution";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { ProductScoresProtection } from "./ProductScoresProtection";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

// Component to redirect while preserving search parameters
const RedirectWithParams = ({ to }: { to: string }) => {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}`} replace />;
};

const ProductScoreDimensionTreeSwitch = ({ mode }: { mode: "comparison" | "evolution" }) => {
    const isProductUxV2 = useIsProductUxV2();

    return <DimensionTreeSwitch appearance={isProductUxV2 ? "product-v2" : "legacy"} mode={mode} />;
};

export const useProductRouter = () => {
    return (
        <Route path="products" errorElement={<RouteErrorPage />}>
            <Route element={<ProductActionProtection slug={VIEW} />}>
                <Route index element={<ProductList />} />
                <Route path="value-chains" element={<ValueChainList />} />
            </Route>

            <Route path="new/:startingPageId?" element={<ProductActionProtection slug={CREATE} />}>
                <Route index element={<CreateQuestionnaire />} />
            </Route>

            <Route path=":productId" element={<ExistingProduct />}>
                <Route path="edit/:startingPageId?" element={<ProductActionProtection slug={MODIFY} />}>
                    <Route index element={<UpdateQuestionnaire />} />
                </Route>

                <Route element={<ProductScoresRoute />}>
                    <Route path="details" element={<ProductActionProtection slug={VIEW} />}>
                        <Route index element={<RedirectWithParams to="scores" />} />

                        <Route element={<SectionResolver />}>
                            <Route element={<ProductDetails />}>
                                <Route
                                    path="scores"
                                    element={
                                        <ProductScoresProtection>
                                            <Outlet />
                                        </ProductScoresProtection>
                                    }
                                >
                                    <Route index element={<ProductScores />} />
                                    <Route path=":scoreId" element={<ProductOneScoreDetail />}>
                                        <Route index element={<ProductScoreDetail selectedTab={null} />} />
                                        <Route
                                            path="distribution"
                                            element={<ProductScoreDetail selectedTab={"distribution"} />}
                                        >
                                            <Route index element={<ProductScoreDistribution />} />
                                        </Route>
                                        <Route
                                            path="comparison"
                                            element={<ProductScoreDetail selectedTab={"comparison"} />}
                                        >
                                            <Route
                                                index
                                                element={<ProductScoreDimensionTreeSwitch mode="comparison" />}
                                            />
                                        </Route>
                                        <Route
                                            path="evolution"
                                            element={<ProductScoreDetail selectedTab={"evolution"} />}
                                        >
                                            <Route
                                                index
                                                element={<ProductScoreDimensionTreeSwitch mode="evolution" />}
                                            />
                                        </Route>
                                    </Route>
                                </Route>

                                <Route element={<ProductActionProtection slug={VIEW} entity={PRODUCT_ACTIVITY_DATA} />}>
                                    <Route path="activities" element={<ProductActivitiesData />} />
                                </Route>

                                <Route path="informations" element={<ProductInformations />} />

                                <Route path="value-chain" element={<ProductValueChain />} />

                                <Route element={<ProductActionProtection slug={VIEW_SSQ} />}>
                                    <Route path="questionnaire" element={<ProductQuestionnaire />}>
                                        <Route index element={<RedirectWithParams to="product" />} />
                                        <Route path="summary" element={<Summary />} />
                                        <Route path="product" element={<Questionnaire />}>
                                            <Route path="company/:productCompanyId">
                                                <Route index element={<Questionnaire />} />
                                            </Route>
                                        </Route>
                                        <Route path="ingredients" element={<Ingredients />} />
                                        <Route path="packagings" element={<Packagings />} />
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                        <Route element={<ProductActionProtection slug={VIEW_SSQ} />}>
                            <Route
                                path="questionnaire/company/:productCompanyId/edit/:headingId"
                                element={
                                    <ProductScoresProtection>
                                        <QuestionnaireUpdate />
                                    </ProductScoresProtection>
                                }
                            />
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Route>
    );
};

type ProductActionProtectionProps = {
    slug: Slugs;
    entity?: Entities;
};
const ProductActionProtection = (props: ProductActionProtectionProps) => {
    const { slug, entity = PRODUCT } = props;
    const { canUser } = useFoodpilotContext();

    if (canUser(slug, entity) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

export const ProductScoresRoute = () => {
    const { product, ssqVersion, isSsqVersionReady, isActivePostLoading } = useProductMetadataContext();

    return (
        <ProductScoresContextProvider
            product={product}
            ssq={ssqVersion?.ssq}
            isSsqReady={isSsqVersionReady}
            isActivePostLoading={isActivePostLoading}
        >
            <Outlet />
        </ProductScoresContextProvider>
    );
};

const ExistingProduct = () => {
    const { productId } = useParams();

    return (
        <EntityScoresProvider entity="product" allowMissingScores>
            <ProductMetadataContextProvider productId={productId}>
                <Outlet />
            </ProductMetadataContextProvider>
        </EntityScoresProvider>
    );
};

const ProductOneScoreDetail = () => {
    const { company } = useCompanyMetadata();
    const { product, ssqVersion, isSsqVersionReady } = useProductMetadataContext();

    const { scoreId } = useParams();

    if (scoreId === undefined || isSsqVersionReady === false) {
        return <Loading />;
    }

    return (
        <ProductOneScoreDetailProvider
            companyId={company.id}
            productId={product.id}
            scoreId={Number(scoreId)}
            ssqVersion={ssqVersion}
        >
            <Outlet />
        </ProductOneScoreDetailProvider>
    );
};
